import * as React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Check from '@material-ui/icons/Check';
import ErrorOutline from '@material-ui/icons/ErrorOutline';
import InfoOutlined from '@material-ui/icons/InfoOutlined';
import clsx from 'clsx';
import get from 'lodash/get';
import getImageUrl from '@configuratorware/configurator-frontendgui/App/Utils/getImageUrl';
import { getItemAttributeValueByIdentifier } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/AttributeSelectors';
import { formatHexColor } from '../../utils/editorUnits';
import { AttributeIdentifiers } from '../../Enums';
import { XXL_COLORS } from '../../theme/colors';
import SectionTitle from './SectionTitle';

const COLOR_SWATCH_SIZE = 36;
const IMAGE_CARD_WIDTH = 150;
const IMAGE_CARD_IMAGE_HEIGHT = 104;

const useStyles = makeStyles((theme) => {
  const tokens = (theme as { xxlprint?: typeof XXL_COLORS }).xxlprint || XXL_COLORS;

  return {
    section: {
      marginBottom: theme.spacing(2),
    },
    tiles: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
    },
    tile: {
      minWidth: 72,
      padding: theme.spacing(1),
      border: `1px solid ${tokens.borderStrong}`,
      borderRadius: 0,
      cursor: 'pointer',
      textAlign: 'center',
      fontSize: 12,
      color: tokens.sectionTitle,
      backgroundColor: tokens.paperBackground,
      '&:hover': {
        borderColor: tokens.accent,
      },
    },
    tileSelected: {
      borderColor: tokens.tileSelectedBorder,
      backgroundColor: tokens.tileSelectedBg,
      color: tokens.sectionTitle,
      fontWeight: 600,
    },
    tileDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    segmented: {
      display: 'flex',
      width: '100%',
      border: `1px solid ${tokens.borderStrong}`,
    },
    segmentedDisabled: {
      opacity: 0.5,
    },
    segmentButton: {
      flex: 1,
      padding: theme.spacing(1, 1.5),
      border: 'none',
      borderRight: `1px solid ${tokens.borderStrong}`,
      backgroundColor: tokens.paperBackground,
      color: tokens.sectionTitle,
      fontFamily: 'inherit',
      fontWeight: 700,
      fontSize: 13,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      cursor: 'pointer',
      transition: 'background-color 0.12s ease, color 0.12s ease',
      '&:last-child': { borderRight: 'none' },
      '&:hover': { backgroundColor: tokens.surfaceMuted },
      '&:disabled': {
        cursor: 'not-allowed',
        color: tokens.borderMedium,
        backgroundColor: tokens.surfaceMuted,
      },
    },
    segmentButtonActive: {
      backgroundColor: tokens.accent,
      color: tokens.surfaceDark,
      '&:hover': { backgroundColor: tokens.accent },
    },
    note: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
      marginTop: theme.spacing(1.25),
      padding: theme.spacing(1, 1.25),
      backgroundColor: tokens.surfaceMuted,
      borderLeft: `3px solid ${tokens.accent}`,
      fontSize: 12,
      lineHeight: 1.4,
      color: tokens.sectionTitle,
    },
    noteWarning: {
      borderLeftColor: theme.palette.error.main,
    },
    noteIcon: {
      flexShrink: 0,
      fontSize: 16,
      marginTop: 1,
      color: tokens.accent,
    },
    noteIconWarning: {
      color: theme.palette.error.main,
    },
    noteText: {
      flex: 1,
    },
    noteAction: {
      flexShrink: 0,
      alignSelf: 'center',
      padding: theme.spacing(0.5, 1.25),
      border: `1px solid ${tokens.borderStrong}`,
      borderRadius: 0,
      backgroundColor: tokens.paperBackground,
      color: tokens.sectionTitle,
      fontFamily: 'inherit',
      fontWeight: 700,
      fontSize: 11,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      cursor: 'pointer',
      '&:hover': {
        borderColor: tokens.accent,
      },
    },
    colorGrid: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
    },
    colorSwatch: {
      position: 'relative',
      width: COLOR_SWATCH_SIZE,
      height: COLOR_SWATCH_SIZE,
      boxSizing: 'border-box',
      border: `1px solid ${tokens.borderStrong}`,
      borderRadius: 0,
      padding: 0,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'box-shadow 0.1s ease',
      '&:hover': {
        boxShadow: `0 0 0 2px ${tokens.paperBackground}, 0 0 0 3px ${tokens.borderStrong}`,
      },
    },
    colorSwatchSelected: {
      boxShadow: `0 0 0 2px ${tokens.paperBackground}, 0 0 0 4px ${tokens.accent}`,
      '&:hover': {
        boxShadow: `0 0 0 2px ${tokens.paperBackground}, 0 0 0 4px ${tokens.accent}`,
      },
    },
    colorSwatchDisabled: {
      opacity: 0.4,
      cursor: 'not-allowed',
    },
    colorCheck: {
      color: '#ffffff',
      fontSize: 18,
      filter: 'drop-shadow(0 0 1px rgba(0, 0, 0, 0.55))',
      pointerEvents: 'none',
    },
    colorCaption: {
      marginBottom: theme.spacing(1),
      minHeight: 18,
      fontSize: 12,
      lineHeight: 1.4,
      color: tokens.textBase,
    },
    colorCaptionName: {
      fontWeight: 700,
      color: tokens.sectionTitle,
    },
    imageCards: {
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fill, minmax(${IMAGE_CARD_WIDTH}px, 1fr))`,
      gap: theme.spacing(1.5),
    },
    imageCard: {
      position: 'relative',
      width: '100%',
      border: `1px solid ${tokens.borderStrong}`,
      backgroundColor: tokens.paperBackground,
      cursor: 'pointer',
      padding: 0,
      textAlign: 'left',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      transition: 'border-color 0.12s ease, background-color 0.12s ease',
      '&:hover': {
        borderColor: tokens.accent,
      },
    },
    imageCardSelected: {
      borderColor: tokens.tileSelectedBorder,
      backgroundColor: tokens.selectedTint,
      boxShadow: `inset 0 0 0 1px ${tokens.tileSelectedBorder}`,
    },
    cardNumber: {
      position: 'absolute',
      top: 6,
      right: 8,
      zIndex: 1,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: '0.04em',
      color: tokens.borderMedium,
      fontVariantNumeric: 'tabular-nums',
      pointerEvents: 'none',
    },
    imageCardDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    imageArea: {
      position: 'relative',
      width: '100%',
      height: IMAGE_CARD_IMAGE_HEIGHT,
      backgroundColor: tokens.pageBackground,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    },
    imageAreaImg: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      display: 'block',
      transition: 'opacity 0.25s ease',
    },
    imageAreaImgLoading: {
      opacity: 0,
    },
    '@keyframes optionTileShimmer': {
      '0%': { opacity: 1 },
      '50%': { opacity: 0.4 },
      '100%': { opacity: 1 },
    },
    imageSkeleton: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: tokens.borderLight,
      animation: '$optionTileShimmer 1.2s ease-in-out infinite',
      pointerEvents: 'none',
    },
    imagePlaceholder: {
      width: '100%',
      height: '100%',
      backgroundColor: tokens.pageBackground,
    },
    imageText: {
      padding: theme.spacing(1.25, 1.25, 1.5),
      borderTop: `1px solid ${tokens.borderLight}`,
    },
    imageTitle: {
      fontSize: 14,
      lineHeight: 1.25,
      fontWeight: 800,
      letterSpacing: '0.01em',
      color: tokens.sectionTitle,
    },
    imageTitleSelected: {
      color: tokens.sectionTitle,
    },
    imageSubtitle: {
      marginTop: 2,
      fontSize: 12,
      lineHeight: 1.3,
      fontWeight: 400,
      color: tokens.textBase,
    },
  };
});

type OptionLike = {
  identifier: string;
  title: string;
  thumbnail?: string | null;
  abstract?: string;
  description?: string;
  attributes?: unknown[];
};

type OptionTilesProps = {
  title: string;
  options: OptionLike[];
  selectedIdentifier: string | string[] | null;
  onSelect: (option: OptionLike) => void;
  disabled?: boolean;
  showColorSwatch?: boolean;
  showImageCard?: boolean;
  /** Render a two-up segmented control (for binary named choices like matt/gloss). */
  segmented?: boolean;
  /** Option identifiers to disable individually (e.g. an incompatible finish segment). */
  disabledOptionIds?: string[];
  /** Called instead of onSelect when a disabled tile is clicked (e.g. to explain why it's blocked). */
  onDisabledOptionSelect?: (option: OptionLike) => void;
  /** Optional descriptive note rendered below the control. */
  note?: React.ReactNode;
  /** Visual variant for the note box. 'warning' uses red instead of cyan. */
  noteVariant?: 'info' | 'warning';
  /** Label for the note's dismiss button; rendered only together with onNoteAction. */
  noteActionLabel?: string;
  /** Called when the note's dismiss button is clicked. */
  onNoteAction?: () => void;
  /** Resolve a tile's preview image URL directly, bypassing the default thumbnail lookup. */
  getThumbnailUrl?: (option: OptionLike) => string | null;
  /** Resolve the full-size image to prefetch on hover/focus so selecting it swaps in instantly. */
  getPreloadUrl?: (option: OptionLike) => string | null;
};

function resolveThumbnailUrl(thumbnail: string): string | null {
  if (!thumbnail) return null;
  if (/^https?:\/\//i.test(thumbnail)) return thumbnail;
  return getImageUrl(thumbnail) || null;
}

function getOptionSubtitle(option: OptionLike): string {
  return get(option, 'abstract', '') || get(option, 'description', '') || '';
}

function ImageCardTile({
  option,
  index,
  isSelected,
  disabled,
  onSelect,
  classes,
  getThumbnailUrl,
  getPreloadUrl,
}: {
  option: OptionLike;
  index: number;
  isSelected: boolean;
  disabled: boolean;
  onSelect: (option: OptionLike) => void;
  classes: ReturnType<typeof useStyles>;
  getThumbnailUrl?: (option: OptionLike) => string | null;
  getPreloadUrl?: (option: OptionLike) => string | null;
}) {
  const thumbnailUrl = getThumbnailUrl
    ? getThumbnailUrl(option)
    : resolveThumbnailUrl(get(option, 'thumbnail', ''));
  const subtitle = getOptionSubtitle(option);
  const [imageFailed, setImageFailed] = React.useState(false);
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const imgRef = React.useRef<HTMLImageElement>(null);
  const showImage = Boolean(thumbnailUrl) && !imageFailed;

  // Prefetch the full-size image once on first hover/focus so clicking swaps it onto the
  // canvas with no visible load. The tile itself only ever shows the small thumbnail.
  const preloadedRef = React.useRef(false);
  const preloadFull = React.useCallback(() => {
    if (preloadedRef.current || !getPreloadUrl) return;
    const url = getPreloadUrl(option);
    if (!url) return;
    preloadedRef.current = true;
    const img = new Image();
    img.src = url;
  }, [getPreloadUrl, option]);

  React.useEffect(() => {
    setImageFailed(false);
    setImageLoaded(false);
    // onLoad may not fire for an already-cached image — catch that synchronously.
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setImageLoaded(true);
    }
  }, [thumbnailUrl]);

  return (
    <button
      type="button"
      className={clsx(
        classes.imageCard,
        isSelected && classes.imageCardSelected,
        disabled && classes.imageCardDisabled
      )}
      onClick={() => !disabled && onSelect(option)}
      onMouseEnter={preloadFull}
      onFocus={preloadFull}
      aria-pressed={isSelected}
      aria-label={option.title}
      disabled={disabled}
    >
      <div className={classes.imageArea}>
        {showImage ? (
          <>
            <img
              ref={imgRef}
              src={thumbnailUrl!}
              alt=""
              loading="lazy"
              decoding="async"
              className={clsx(classes.imageAreaImg, !imageLoaded && classes.imageAreaImgLoading)}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageFailed(true)}
            />
            {!imageLoaded && <span className={classes.imageSkeleton} aria-hidden />}
          </>
        ) : (
          <div className={classes.imagePlaceholder} />
        )}
      </div>
      <div className={classes.imageText}>
        <div className={clsx(classes.imageTitle, isSelected && classes.imageTitleSelected)}>
          {option.title}
        </div>
        {subtitle ? <div className={classes.imageSubtitle}>{subtitle}</div> : null}
      </div>
    </button>
  );
}

export default function OptionTiles({
  title,
  options,
  selectedIdentifier,
  onSelect,
  disabled = false,
  showColorSwatch = false,
  showImageCard = false,
  segmented = false,
  disabledOptionIds,
  onDisabledOptionSelect,
  note,
  noteVariant = 'info',
  noteActionLabel,
  onNoteAction,
  getThumbnailUrl,
  getPreloadUrl,
}: OptionTilesProps) {
  const classes = useStyles();
  const [hoveredColorId, setHoveredColorId] = React.useState<string | null>(null);

  return (
    <div className={classes.section}>
      {title ? <SectionTitle>{title}</SectionTitle> : null}

      {segmented ? (
        <div className={clsx(classes.segmented, disabled && classes.segmentedDisabled)} role="group">
          {options.map((option) => {
            const isSelected = Array.isArray(selectedIdentifier)
              ? selectedIdentifier.includes(option.identifier)
              : selectedIdentifier === option.identifier;
            const optionDisabled = disabled || Boolean(disabledOptionIds?.includes(option.identifier));

            return (
              <button
                key={option.identifier}
                type="button"
                className={clsx(classes.segmentButton, isSelected && classes.segmentButtonActive)}
                onClick={() => !optionDisabled && onSelect(option)}
                aria-pressed={isSelected}
                disabled={optionDisabled}
              >
                {option.title}
              </button>
            );
          })}
        </div>
      ) : showColorSwatch ? (
        <>
          <div className={classes.colorCaption}>
            {(() => {
              const isMatch = (o: OptionLike) =>
                Array.isArray(selectedIdentifier)
                  ? selectedIdentifier.includes(o.identifier)
                  : selectedIdentifier === o.identifier;
              const caption = options.find((o) => o.identifier === hoveredColorId) || options.find(isMatch);
              return caption ? <span className={classes.colorCaptionName}>{caption.title}</span> : ' ';
            })()}
          </div>
          <div className={classes.colorGrid}>
            {options.map((option) => {
              const hex = formatHexColor(
                getItemAttributeValueByIdentifier(option, AttributeIdentifiers.hex, '000000')
              );
              const isSelected = Array.isArray(selectedIdentifier)
                ? selectedIdentifier.includes(option.identifier)
                : selectedIdentifier === option.identifier;

              return (
                <button
                  key={option.identifier}
                  type="button"
                  className={clsx(
                    classes.colorSwatch,
                    isSelected && classes.colorSwatchSelected,
                    disabled && classes.colorSwatchDisabled
                  )}
                  style={{ backgroundColor: hex }}
                  onClick={() => !disabled && onSelect(option)}
                  onMouseEnter={() => setHoveredColorId(option.identifier)}
                  onMouseLeave={() => setHoveredColorId((id) => (id === option.identifier ? null : id))}
                  onFocus={() => setHoveredColorId(option.identifier)}
                  onBlur={() => setHoveredColorId((id) => (id === option.identifier ? null : id))}
                  aria-pressed={isSelected}
                  aria-label={option.title}
                  title={option.title}
                  disabled={disabled}
                >
                  {isSelected && <Check className={classes.colorCheck} aria-hidden />}
                </button>
              );
            })}
          </div>
        </>
      ) : showImageCard ? (
        <div className={classes.imageCards}>
          {options.map((option, index) => (
            <ImageCardTile
              key={option.identifier}
              option={option}
              index={index}
              isSelected={
                Array.isArray(selectedIdentifier)
                  ? selectedIdentifier.includes(option.identifier)
                  : selectedIdentifier === option.identifier
              }
              disabled={disabled}
              onSelect={onSelect}
              classes={classes}
              getThumbnailUrl={getThumbnailUrl}
              getPreloadUrl={getPreloadUrl}
            />
          ))}
        </div>
      ) : (
        <div className={classes.tiles}>
          {options.map((option) => {
            const isSelected = Array.isArray(selectedIdentifier)
              ? selectedIdentifier.includes(option.identifier)
              : selectedIdentifier === option.identifier;
            const optionDisabled = disabled || Boolean(disabledOptionIds?.includes(option.identifier));

            return (
              <div
                key={option.identifier}
                className={clsx(
                  classes.tile,
                  isSelected && classes.tileSelected,
                  optionDisabled && classes.tileDisabled
                )}
                onClick={() =>
                  optionDisabled ? onDisabledOptionSelect?.(option) : onSelect(option)
                }
                role="button"
                tabIndex={0}
                aria-disabled={optionDisabled}
              >
                {option.title}
              </div>
            );
          })}
        </div>
      )}

      {note ? (
        <div className={clsx(classes.note, noteVariant === 'warning' && classes.noteWarning)}>
          {noteVariant === 'warning'
            ? <ErrorOutline className={clsx(classes.noteIcon, classes.noteIconWarning)} fontSize="small" aria-hidden />
            : <InfoOutlined className={classes.noteIcon} fontSize="small" aria-hidden />
          }
          <span className={classes.noteText}>{note}</span>
          {onNoteAction && noteActionLabel ? (
            <button type="button" className={classes.noteAction} onClick={onNoteAction}>
              {noteActionLabel}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
